import { createSelector } from 'reselect';
import type { RootState } from '../index';
import type { PermissionAction, SectionPermission } from '@/api/domains/authorization/permission/permissions.types';


// Base selector
const selectPermissionState = (state: RootState) => state.permissions;

export const selectPermissionsMap = createSelector(
  [selectPermissionState],
  (permissions) => permissions.permissions,
);

export const selectPermissionsStatus = createSelector(
  [selectPermissionState],
  (permissions) => permissions.status,
);

/** Factory selector: returns the ScreenPermission for a given route code, or null. */
export const selectScreenPermission = (code: string) =>
  createSelector(
    [selectPermissionsMap],
    (permissions) => permissions[code] ?? null,
  );

/** Factory selector: checks if the user can perform a specific action on a screen. */
export const selectHasScreenAction = (code: string, action: PermissionAction) =>
  createSelector(
    [selectPermissionsMap],
    (permissions) => {
      const permission = permissions[code];
      if (!permission) return false;
      const actionMap: Record<PermissionAction, boolean> = {
        view: permission.canView,
        create: permission.canCreate,
        edit: permission.canEdit,
        delete: permission.canDelete,
      };
      return actionMap[action] ?? false;
    },
  );

/** Factory selector: returns section-level permission flags for a given screen + section. */
export const selectSectionPermission = (code: string, sectionName: string) =>
  createSelector(
    [selectPermissionsMap],
    (permissions): SectionPermission | null => {
      const permission = permissions[code];
      if (!permission) return null;
      return permission.sections[sectionName] ?? null;
    },
  );
